import { useEffect, useRef, useMemo, useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import * as d3 from 'd3';
import { useContainerWidth } from '../../hooks/useContainerWidth';
import { createSeededRandom } from '../../utils/seededRandom';
import { generateProbSeries } from '../../api/mockData';
import { PreviewBadge, PanelHeader, SectionCard } from '../shared';
import type { ProbabilityPoint } from '../../api/types';
import { drawPercentAxis, drawAxisFitButton } from './chartAxis';
import { fitProbabilityDomain, clampToDomain } from './chartYDomain';
import { useAxisFit } from '../../hooks/useAxisFit';

interface ProbabilityChartProps {
    resolved?: boolean;
    outcome?: string;
    prob?: number;
}

const MARGIN = { top: 12, right: 12, bottom: 30, left: 40 };

export function ProbabilityChart({
    resolved,
    outcome,
    prob = 0.65,
}: ProbabilityChartProps) {
    const theme = useTheme();
    const { ref: containerRef, width } = useContainerWidth();
    const svgRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [showTrades, setShowTrades] = useState(true);
    const [showVolume, setShowVolume] = useState(true);
    const { fit, toggleFit } = useAxisFit();

    // TODO: Wire to real market-probability-history endpoint when available
    const data = useMemo(
        () =>
            generateProbSeries(
                new Date(2025, 0, 1),
                new Date(2025, 11, 31),
                prob
            ),
        [prob]
    );

    const stableRandom = useMemo(() => {
        const rng = createSeededRandom(data.length);
        return {
            volumeHeights: data.map(() => rng() * 18 + 2),
            tradeMask: data.map(() => rng() > 0.85),
            tradeOffsets: data.map(() => rng() * 6 - 3),
        };
    }, [data]);

    // D3 render
    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const height = 220;
        const w = width - MARGIN.left - MARGIN.right;
        const h = height - MARGIN.top - MARGIN.bottom;

        if (w <= 0 || h <= 0) return;

        const isDark = theme.palette.mode === 'dark';
        const gridColor = isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06);
        const textColor = theme.palette.text.disabled;
        const lineColor = theme.palette.primary.main;

        // Scales
        const xScale = d3
            .scaleTime()
            .domain(d3.extent(data, d => d.t) as [Date, Date])
            .range([MARGIN.left, MARGIN.left + w]);

        const probDomain = fitProbabilityDomain(
            data.map(d => d.prob),
            fit
        );
        const yScale = d3
            .scaleLinear()
            .domain(probDomain.domain)
            .range([MARGIN.top + h, MARGIN.top]);

        // Grid + Y labels
        drawPercentAxis(svg, {
            ticks: probDomain.ticks,
            y: v => yScale(v),
            x1: MARGIN.left,
            x2: MARGIN.left + w,
            labelSide: 'left',
            gridColor,
            textColor,
            fontSize: '10px',
            decimals: probDomain.decimals,
        });
        drawAxisFitButton(svg, {
            x: MARGIN.left - 26,
            y: MARGIN.top - 26,
            fit,
            accentColor: lineColor,
            mutedColor: textColor,
            onToggle: toggleFit,
        });

        // X axis
        const xTicks = xScale.ticks(6);
        svg.append('g')
            .selectAll('text')
            .data(xTicks)
            .join('text')
            .attr('x', d => xScale(d))
            .attr('y', height - 6)
            .attr('fill', textColor)
            .attr('font-size', '10px')
            .attr('text-anchor', 'middle')
            .text(d => d3.timeFormat('%b')(d));

        // Area gradient
        const gradId = 'prob-area-grad';
        const defs = svg.append('defs');
        const grad = defs
            .append('linearGradient')
            .attr('id', gradId)
            .attr('x1', '0')
            .attr('y1', '0')
            .attr('x2', '0')
            .attr('y2', '1');
        grad.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', lineColor)
            .attr('stop-opacity', 0.12);
        grad.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', lineColor)
            .attr('stop-opacity', 0);

        // Area
        const area = d3
            .area<ProbabilityPoint>()
            .x(d => xScale(d.t))
            .y0(MARGIN.top + h)
            .y1(d => yScale(d.prob));

        svg.append('path')
            .datum(data)
            .attr('d', area)
            .attr('fill', `url(#${gradId})`);

        // Line
        const line = d3
            .line<ProbabilityPoint>()
            .x(d => xScale(d.t))
            .y(d => yScale(d.prob));

        svg.append('path')
            .datum(data)
            .attr('d', line)
            .attr('fill', 'none')
            .attr('stroke', lineColor)
            .attr('stroke-width', 1.8);

        // Volume bars (mock)
        if (showVolume) {
            const barWidth = Math.max(1, w / data.length - 1);
            svg.append('g')
                .selectAll('rect')
                .data(data)
                .join('rect')
                .attr('x', d => xScale(d.t) - barWidth / 2)
                .attr('y', MARGIN.top + h - 20)
                .attr('width', barWidth)
                .attr('height', (_, i) => stableRandom.volumeHeights[i])
                .attr('fill', alpha(lineColor, 0.15))
                .attr('rx', 1);
        }

        // Trade dots (mock)
        if (showTrades) {
            const tradeSample = data
                .map((d, i) => ({ d, i }))
                .filter((_, i) => stableRandom.tradeMask[i]);
            svg.append('g')
                .selectAll('circle')
                .data(tradeSample)
                .join('circle')
                .attr('cx', ({ d }) => xScale(d.t))
                .attr(
                    'cy',
                    ({ d, i }) =>
                        yScale(clampToDomain(d.prob, probDomain.domain)) +
                        stableRandom.tradeOffsets[i]
                )
                .attr('r', 2)
                .attr('fill', ({ d }) =>
                    d.prob > 0.5
                        ? theme.palette.success.main
                        : theme.palette.error.main
                )
                .attr('opacity', 0.5);
        }

        // Resolution marker
        if (resolved) {
            const resColor =
                outcome === 'YES' || outcome === 'yes'
                    ? theme.palette.success.main
                    : theme.palette.error.main;
            const lastX = xScale(data[data.length - 1].t);
            svg.append('line')
                .attr('x1', lastX)
                .attr('y1', MARGIN.top)
                .attr('x2', lastX)
                .attr('y2', MARGIN.top + h)
                .attr('stroke', resColor)
                .attr('stroke-width', 1.5);
            svg.append('text')
                .attr('x', lastX - 4)
                .attr('y', MARGIN.top + 12)
                .attr('fill', resColor)
                .attr('font-size', '10px')
                .attr('text-anchor', 'end')
                .attr('font-family', 'monospace')
                .attr('font-weight', '600')
                .text(`Resolved ${outcome}`);
        }

        // Crosshair + tooltip
        const crosshairGroup = svg.append('g').style('display', 'none');
        const crosshairV = crosshairGroup
            .append('line')
            .attr('stroke', textColor)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '3 3');
        const crosshairH = crosshairGroup
            .append('line')
            .attr('stroke', textColor)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '3 3');
        const crosshairDot = crosshairGroup
            .append('circle')
            .attr('r', 3)
            .attr('fill', lineColor)
            .attr('stroke', isDark ? '#000' : '#fff')
            .attr('stroke-width', 1.5);

        const tooltip = d3.select(tooltipRef.current);
        const bisect = d3.bisector<ProbabilityPoint, Date>(d => d.t).left;

        svg.append('rect')
            .attr('x', MARGIN.left)
            .attr('y', MARGIN.top)
            .attr('width', w)
            .attr('height', h)
            .attr('fill', 'transparent')
            .on('mouseenter', () => {
                crosshairGroup.style('display', null);
                tooltip.style('display', 'block');
            })
            .on('mouseleave', () => {
                crosshairGroup.style('display', 'none');
                tooltip.style('display', 'none');
            })
            .on('mousemove', event => {
                const [mx] = d3.pointer(event);
                const date = xScale.invert(mx);
                const idx = bisect(data, date, 1);
                const d0 = data[idx - 1];
                const d1 = data[idx];
                if (!d0) return;
                const d =
                    d1 &&
                    date.getTime() - d0.t.getTime() >
                        d1.t.getTime() - date.getTime()
                        ? d1
                        : d0;

                const cx = xScale(d.t);
                const cy = yScale(clampToDomain(d.prob, probDomain.domain));

                crosshairV
                    .attr('x1', cx)
                    .attr('y1', MARGIN.top)
                    .attr('x2', cx)
                    .attr('y2', MARGIN.top + h);
                crosshairH
                    .attr('x1', MARGIN.left)
                    .attr('y1', cy)
                    .attr('x2', MARGIN.left + w)
                    .attr('y2', cy);
                crosshairDot.attr('cx', cx).attr('cy', cy);

                tooltip
                    .style('left', `${cx + 12}px`)
                    .style('top', `${cy - 30}px`)
                    .html(
                        `<div style="font-size:10px;color:${textColor};margin-bottom:2px">${d3.timeFormat('%b %d, %Y')(d.t)}</div>` +
                            `<div style="font-family:monospace;font-size:14px;font-weight:700">${(d.prob * 100).toFixed(1)}%</div>`
                    );
            });
    }, [
        data,
        width,
        theme,
        resolved,
        outcome,
        showTrades,
        showVolume,
        stableRandom,
        fit,
        toggleFit,
    ]);

    return (
        <SectionCard>
            <PanelHeader title="YES Probability">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PreviewBadge />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={showTrades}
                                onChange={e => setShowTrades(e.target.checked)}
                                sx={{ p: 0.25 }}
                            />
                        }
                        label="Trades"
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                fontSize: '11px',
                                color: 'text.secondary',
                            },
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={showVolume}
                                onChange={e => setShowVolume(e.target.checked)}
                                sx={{ p: 0.25 }}
                            />
                        }
                        label="Volume"
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                fontSize: '11px',
                                color: 'text.secondary',
                            },
                        }}
                    />
                </Box>
            </PanelHeader>
            <Box ref={containerRef} sx={{ position: 'relative', p: 1.5 }}>
                <svg
                    ref={svgRef}
                    width={width}
                    height={220}
                    style={{ display: 'block' }}
                />
                <Box
                    ref={tooltipRef}
                    sx={{
                        display: 'none',
                        position: 'absolute',
                        pointerEvents: 'none',
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '4px',
                        px: 1,
                        py: 0.5,
                        zIndex: 10,
                        boxShadow: 1,
                    }}
                />
            </Box>
        </SectionCard>
    );
}
